import AssigneeGroup from '../components/inputs/AssigneeInputGroup';
import AssigneeSingle from '../components/inputs/AssigneeInputSingle';
import AssignTagsGroup from '../components/inputs/AssignTagsGroup';
import AssignTagsSingle from '../components/inputs/AssignTagsSingle';
import CustomFieldGroup from '../components/inputs/CustomFieldGroup';
import CustomFieldSingle from '../components/inputs/CustomFieldSingle';
import FirmwareInputGroup from '../components/inputs/FirmwareInputGroup';
import FirmwareInputSingle from '../components/inputs/FirmwareInputSingle';
import IdentificationInputGroup from '../components/inputs/IdentificationInputGroup';
import IdentificationInputSingle from '../components/inputs/IdentificationInputSingle';
import LicenseGroup from '../components/inputs/LicenseGroup';
import LicenseSingle from '../components/inputs/LicenseSingle';
import OwnershipGroup from '../components/inputs/OwnershipGroup';
import OwnershipSingle from '../components/inputs/OwnershipSingle';
import PurchaseInformationGroup from '../components/inputs/PurchaseInformationGroup';
import PurchaseInformationSingle from '../components/inputs/PurchaseInformationSingle';
import StatusGroup from '../components/inputs/StatusGroup';
import StatusSingle from '../components/inputs/StatusSingle';
import {
  ConfigInputGroup,
  ConfigInputSingle,
} from '../components/inputs/types';
import {
  extractFieldId,
  getGlobalInputProps,
  getInputProps,
} from '../components/inputs/utils';
import { IFormDataOnboardingModal } from '../OnBoardingModal';
import { NeededFromEq } from '../OnBoardingModal.utils';
import { ChildrenT } from './HeadingsCalc';
import type { RightHeadingProps } from '../components/RightHeading';
import type { RightSideProps } from '../components/RightSide';

export type ConfigGroup = {
  kind: 'GROUP';
  globalViewForm: RightHeadingProps['globalViewForm'];
  globalFormV: RightHeadingProps['globalFormV'];
};

export type ConfigSingle = {
  kind: 'SINGLE';
  equipment: NeededFromEq;
  index: number;
  formV: IFormDataOnboardingModal;
  onlyGroupEdits: boolean;
};

export type InputConfig = ConfigGroup | ConfigSingle;
export interface InputParams {
  setFormV: RightHeadingProps['setFormV'];

  equipmentsCount: number;
  activePage: RightHeadingProps['activePage'];
  viewForm: RightHeadingProps['viewForm'];
  setGlobalFormV: RightHeadingProps['setGlobalFormV'];
  customFieldsQuery: RightHeadingProps['customFields'];
  mode: RightSideProps['mode'];
  config: InputConfig;
}

export function getInputs({ config, ...shared }: InputParams) {
  // Will each be replaced on specific config.kind.
  let formV = {} as ConfigSingle['formV'];
  let inputConfig = {} as ConfigInputSingle;
  let index = 0;
  let globalFormV = {} as ConfigGroup['globalFormV'];
  let globalInputConfig = {} as ConfigInputGroup;

  if (config.kind === 'SINGLE') {
    ({ formV, ...inputConfig } = config);
    ({ index } = inputConfig);
  }
  if (config.kind === 'GROUP') {
    ({ globalFormV, ...globalInputConfig } = config);
  }

  switch (shared.activePage.inputType) {
    case 'Identification': {
      if (config.kind === 'GROUP') {
        const props = getGlobalInputProps(['productId'], globalFormV);
        return (
          <IdentificationInputGroup
            {...shared}
            {...props}
            {...globalInputConfig}
          />
        );
      }

      // props to trigger rerender.
      const props = getInputProps(['serial', 'productId'], formV, index);

      return (
        <IdentificationInputSingle {...props} {...shared} {...inputConfig} />
      );
    }

    case 'Firmware & Software': {
      const propsToGet = [
        'installedFirmwareVersion',
        'installedSoftwareVersion',
      ] as const;
      if (config.kind === 'GROUP') {
        const props = getGlobalInputProps(propsToGet, globalFormV);
        return (
          <FirmwareInputGroup {...shared} {...props} {...globalInputConfig} />
        );
      }
      const props = getInputProps(propsToGet, formV, index);
      return <FirmwareInputSingle {...props} {...shared} {...inputConfig} />;
    }

    case 'Purchase Information': {
      const propsToGet = [
        'purchasedPrice',
        'purchasedFrom',
        'purchasedDate',
      ] as const;

      if (config.kind === 'GROUP') {
        const props = getGlobalInputProps(propsToGet, globalFormV);
        return (
          <PurchaseInformationGroup
            {...shared}
            {...props}
            {...globalInputConfig}
          />
        );
      }

      const props = getInputProps(propsToGet, formV, index);

      return (
        <PurchaseInformationSingle {...props} {...shared} {...inputConfig} />
      );
    }

    case 'Ownership': {
      const propsToGet = ['installDate', 'purchasedDate'] as const;

      if (config.kind === 'GROUP') {
        const props = getGlobalInputProps(propsToGet, globalFormV);
        return <OwnershipGroup {...shared} {...props} {...globalInputConfig} />;
      }

      const props = getInputProps(propsToGet, formV, index);
      return <OwnershipSingle {...props} {...shared} {...inputConfig} />;
    }

    case 'License': {
      const propsToGet = ['licenseStartDate', 'licenseEndDate'] as const;
      if (config.kind === 'GROUP') {
        const props = getGlobalInputProps(propsToGet, globalFormV);
        return <LicenseGroup {...shared} {...props} {...globalInputConfig} />;
      }
      const props = getInputProps(propsToGet, formV, index);
      return <LicenseSingle {...props} {...shared} {...inputConfig} />;
    }
    case 'Assignee': {
      const propsToGet = ['userId'] as const;
      if (config.kind === 'GROUP') {
        const props = getGlobalInputProps(propsToGet, globalFormV);
        return <AssigneeGroup {...shared} {...props} {...globalInputConfig} />;
      }
      const props = getInputProps(propsToGet, formV, index);
      return <AssigneeSingle {...props} {...shared} {...inputConfig} />;
    }
    case 'custom-type-date':
    case 'custom-type-description':
    case 'custom-type-email':
    case 'custom-type-phone':
    case 'custom-type-select':
    case 'custom-type-text':
    case 'custom-type-url':
    case 'custom-type-contact': {
      const id = extractFieldId(shared.activePage.id as ChildrenT);
      const propsToGet = [
        'customFields',
        `customFields.${id}.content`,
      ] as const;

      if (config.kind === 'GROUP') {
        const props = getGlobalInputProps(propsToGet, globalFormV);
        return (
          <CustomFieldGroup {...shared} {...props} {...globalInputConfig} />
        );
      }
      const props = getInputProps(propsToGet, formV, index);
      return <CustomFieldSingle {...props} {...shared} {...inputConfig} />;
    }
    case 'Assign Tags': {
      const propsToGet = ['tagsId'] as const;
      if (config.kind === 'GROUP') {
        const props = getGlobalInputProps(propsToGet, globalFormV);
        return (
          <AssignTagsGroup {...shared} {...props} {...globalInputConfig} />
        );
      }
      const props = getInputProps(propsToGet, formV, index);
      return <AssignTagsSingle {...props} {...shared} {...inputConfig} />;
    }
    case 'Selection': {
      const propsToGet = ['equipmentStatusId'] as const;
      if (config.kind === 'GROUP') {
        const props = getGlobalInputProps(propsToGet, globalFormV);
        return <StatusGroup {...shared} {...props} {...globalInputConfig} />;
      }
      const props = getInputProps(propsToGet, formV, index);
      return <StatusSingle {...props} {...shared} {...inputConfig} />;
    }
    default:
      return <></>;
  }
}

// Inputs
